import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const PaymentTermContext = createContext();
 
export const usePaymentTerms = () => {
  const context = useContext(PaymentTermContext);
  if (!context) {
    throw new Error('usePaymentTerms must be used within a PaymentTermProvider');
  }
  return context;
};

export const PaymentTermProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [paymentTerms, setPaymentTerms] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/payment-terms');

  const getPaymentTerms = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      setPaginate(true)
      let response = await apiInstance.call('payment-terms?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        if(filters?.active){
          if(filters?.active === true){
            query += 'active=1';
          }else{
            query += 'active=0';
          }
         
        }
        response = await apiInstance.call('payment-terms?'+query);  
      }
      
      if (response.status === 200) {
       // setPaginate(response.data.paginate)
        setPaymentTerms(response.data.paymentTerms || []);
       
        
        if(filters){
          return response.data.paymentTerms;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch payment terms information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, getToken,paginate]);

  const getPaymentTermByIdApi = useCallback(async (id, filters = {}) => {
    
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`payment-terms/${id}/edit`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let paymentTerms = {'data':[response.data.paymentTerm]}
      setPaymentTerms(paymentTerms || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/payment-terms/edit/'+id))){
        if (getToken()) {
        
          
          await getPaymentTermByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getPaymentTermByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getPaymentTerms(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getPaymentTerms, searchParams, initialized,hideLoading,setApi]);

  const getPaymentTermById = useCallback(async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`payment-terms/${id}/edit`, 'GET', null, true, false);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.paymentTerm };
      } else {
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch payment term information'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch payment term information'
      });
      return { status: 500, error: error.message };
    }
  }, [handleApiError, getToken]);

  const addPaymentTerm = async (data) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {

      let formData = new FormData();
      
      // Append the data as JSON
      formData.append('data', JSON.stringify(data));

      const response = await apiInstance.call('payment-terms', 'POST', formData, true,true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the payment terms list
        await getPaymentTerms();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Payment Term'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create payment term'
      });
      return { status: 500, error: error.message };
    }
  };

  const updatePaymentTerm = async (id, updatedData) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      let formData = new FormData();
      
      // Append the data as JSON
      formData.append('data', JSON.stringify(updatedData));
       
      // Now send the request with formData only once
      const response = await apiInstance.call(`payment-terms/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the payment terms list
        await getPaymentTerms();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
             errors: response.error.validationErrors,
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update Payment Term'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update payment term'
      });
      return { status: 500, error: error.message };
    }
  };

  const deletePaymentTerm = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`payment-terms/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the payment terms list
        await getPaymentTerms();
        navigate(getRoutePath('/payment-terms'));
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete payment term'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete payment term'
      });
      return { status: 500, error: error.message };
    }
  };

  return (
    <PaymentTermContext.Provider
      value={{
        paymentTerms,
        addPaymentTerm,
        getPaymentTermById,
        updatePaymentTerm,
        deletePaymentTerm,
        getPaymentTerms,
        formErrors,
        clearErrors,
        setPaginate,
        paginate
      }}
    >
      {children}
    </PaymentTermContext.Provider>
  );
}; 